import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { 
  fetchCarouselItems, 
  createCarouselItem, 
  updateCarouselItem, 
  deleteCarouselItem
} from '../services/carousel';
import { getStorageUrl } from '../services/api';
import './Carousel.css';

const Carousel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: '',
    order: 0,
    active: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadCarouselItems();
  }, []);

  const loadCarouselItems = async () => {
    try {
      setLoading(true);
      const data = await fetchCarouselItems();
      // Ensure all image URLs are absolute
      const itemsWithFullUrls = data.map(item => ({
        ...item,
        image: getStorageUrl(item.image) || item.image
      }));
      setItems(itemsWithFullUrls);
    } catch (err) {
      setError('Failed to load carousel items: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview URL for display (not base64, just object URL)
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate image file only when adding (not when editing)
    if (!editingItem && !imageFile) {
      setError('Please select an image file');
      return;
    }

    try {
      const submitData = {
        title: formData.title,
        subtitle: formData.subtitle,
        order: parseInt(formData.order) || 0,
        active: formData.active,
      };

      if (editingItem) {
        // Update: only title, subtitle, order, active (no image change)
        await updateCarouselItem(editingItem.id, submitData);
        setSuccess('Carousel item updated successfully!');
      } else {
        await createCarouselItem({ ...submitData, image: imageFile });
        setSuccess('Carousel item created successfully!');
      }
      
      setShowModal(false);
      resetForm();
      loadCarouselItems();
    } catch (err) {
      setError('Failed to save carousel item: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      subtitle: item.subtitle || '',
      order: item.order || 0,
      active: item.active !== undefined ? item.active : true,
    });
    // Ensure image URL is absolute for preview
    const imageUrl = getStorageUrl(item.image) || item.image;
    setImagePreview(imageUrl);
    setImageFile(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carousel item?')) {
      return;
    }

    try {
      await deleteCarouselItem(id);
      setSuccess('Carousel item deleted successfully!');
      loadCarouselItems();
    } catch (err) {
      setError('Failed to delete carousel item: ' + (err.response?.data?.message || err.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      order: 0,
      active: true,
    });
    setImageFile(null);
    // Revoke object URL to free memory
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setEditingItem(null);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <div className="carousel-page">
      <AdminPageTitle title="Carousel" />
      <div className="page-header">
        <h1>Carousel Management</h1>
        <button className="btn-primary" onClick={handleAddNew}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add New Carousel Item
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading carousel items...</div>
      ) : (
        <div className="carousel-grid">
          {items.length === 0 ? (
            <div className="empty-state">
              <p>No carousel items found. Click "Add New Carousel Item" to create one.</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="carousel-card">
                <div className="carousel-card-image">
                  <img src={item.image} alt={item.title} />
                  {!item.active && <div className="inactive-badge">Inactive</div>}
                </div>
                <div className="carousel-card-content">
                  <h3>{item.title}</h3>
                  {item.subtitle && <p className="subtitle">{item.subtitle}</p>}
                  <div className="carousel-card-meta">
                    <span>Order: {item.order}</span>
                    <span className={`status ${item.active ? 'active' : 'inactive'}`}>
                      {item.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="carousel-card-actions">
                    <button className="btn-edit" onClick={() => handleEdit(item)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(item.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Carousel Item' : 'Add New Carousel Item'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="carousel-form">
              <div className="form-group">
                <label htmlFor="title">Title (optional)</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter carousel title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subtitle">Subtitle</label>
                <input
                  type="text"
                  id="subtitle"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  placeholder="Enter carousel subtitle (optional)"
                />
              </div>

              {!editingItem && (
                <div className="form-group">
                  <label htmlFor="image">Image *</label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    required
                  />
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                      {imageFile && <p className="image-note">Selected: {imageFile.name}</p>}
                    </div>
                  )}
                </div>
              )}
              {editingItem && editingItem.image && (
                <div className="form-group">
                  <label>Current image</label>
                  <div className="image-preview">
                    <img src={getStorageUrl(editingItem.image) || editingItem.image} alt="Current" />
                  </div>
                  <p className="image-note">To change the image, delete this item and add a new one.</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="order">Display Order</label>
                <input
                  type="number"
                  id="order"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                />
                <small>Lower numbers appear first</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  Active (visible on homepage)
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingItem ? 'Update' : 'Create'} Carousel Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel;
